import type { TrackerCopy } from "./types";

/** Inhalte des Santa-Trackers auf Deutsch. */
export const TRACKER_COPY_DE: TrackerCopy = {
  meta: {
    title:
      "Wo ist der Weihnachtsmann? Weihnachts-Countdown und Weihnachtsmann-Tracker",
    description:
      "Sieh, wie lange es noch bis Weihnachten dauert, verfolge die Reise des Weihnachtsmanns um die Welt und erfahre, wann er in deiner Zeitzone ankommen kann.",
    keywords: [
      "wo ist der Weihnachtsmann",
      "Weihnachtsmann-Tracker",
      "Weihnachts-Countdown",
      "wie lange bis Weihnachten",
      "wie viele Tage bis Weihnachten",
      "wann kommt der Weihnachtsmann",
      "Route des Weihnachtsmanns",
      "Weihnachtsmann verfolgen",
      "Weihnachtsmann Karte",
      "Weihnachtsabend Tracker",
    ],
    schemaName: "Weihnachtsmann-Tracker und Weihnachts-Countdown",
  },
  hero: {
    badge: {
      preparing: "Werkstatt am Nordpol",
      liftoff: "Letzte Vorbereitungen",
      traveling: "Auslieferung läuft",
      completed: "Mission erfüllt",
    },
    h1: {
      preparing: "Wie lange dauert es noch, bis der Weihnachtsmann kommt?",
      liftoff: "Der Weihnachtsmann hebt gleich ab",
      traveling: "Wo ist der Weihnachtsmann gerade?",
      completed: "Der Weihnachtsmann ist zurück am Nordpol",
    },
    intro: {
      preparing:
        "Am Nordpol steht alles nie still: Die Elfen verpacken Geschenke, die Rentiere trainieren und die große Liste wird immer wieder geprüft. Hier siehst du, wie lange es noch bis Weihnachten dauert und wie die Vorbereitungen vorankommen.",
      liftoff:
        "Nur noch wenige Stunden bis zur längsten Reise des Jahres. Der Schlitten ist beladen, Rudolfs Nase leuchtet und am Nordpol schläft niemand.",
      traveling:
        "Es ist Heiligabend und der Schlitten reist um die Welt. Verfolge die Route auf der Karte, sieh, wo der Weihnachtsmann gerade war, und erfahre, wann er bei dir ankommen kann.",
      completed:
        "Alle Geschenke sind ausgeliefert und die Rentiere ruhen sich am Nordpol aus. Weihnachten im nächsten Jahr wird schon vorbereitet – hier ist der Countdown.",
    },
    toMap: "Karte ansehen",
  },
  countdown: {
    heading: {
      preparing: "Der Weihnachtsmann kommt in",
      liftoff: "Der Schlitten hebt ab in",
      traveling: "Er kommt in",
      completed: "Das nächste Weihnachten ist in",
    },
    units: {
      days: "Tagen",
      hours: "Stunden",
      minutes: "Minuten",
      seconds: "Sekunden",
    },
    christmasNote: "Heiligabend ist am 24. Dezember: noch {days} Tage.",
    christmasToday: "Heute ist Weihnachten!",
    arrivingNow: "Er fliegt gerade über deine Region!",
    ariaLabel: "Countdown bis zur Ankunft des Weihnachtsmanns",
  },
  status: {
    preparing: {
      far: [
        "Die Elfen bauen schon die ersten Spielzeuge des Jahres.",
        "Der Weihnachtsmann hat ein neues Notizbuch für die große Liste begonnen.",
        "In der Werkstatt werden Geschenkideen für das nächste Weihnachten getestet.",
        "Die Rentiere kommen langsam aus ihrem Urlaub zurück.",
      ],
      months: [
        "Die Werkstatt am Nordpol arbeitet ohne Pause.",
        "Der Weihnachtsmann geht seine große Liste durch.",
        "Die Rentiere haben mit dem Training begonnen.",
        "Die ersten Briefe aus aller Welt treffen ein.",
      ],
      weeks: [
        "Frau Claus backt Kekse für die ganze Werkstatt.",
        "Der Weihnachtsmann prüft die Liste zum zweiten Mal.",
        "Die Elfen verpacken die Geschenke mit Hochdruck.",
        "Der Schlitten steht zur Wartung in der Werkstatt.",
      ],
      days: [
        "Der Start des Schlittens steht unmittelbar bevor!",
        "Die Elfen schließen die letzten Säcke.",
        "Rudolf trainiert für die große Nacht.",
        "Am Nordpol schläft schon niemand mehr.",
      ],
      hours: [
        "Der Weihnachtsmann lädt die letzten Geschenke ein.",
        "Der Schlitten ist startbereit.",
        "Die Rentiere sind bereits angespannt und warten auf das Signal.",
        "Rudolfs Nase leuchtet schon.",
      ],
    },
    liftoff: [
      "Der Weihnachtsmann lädt die letzten Geschenke ein.",
      "Rudolfs Nase leuchtet schon.",
      "Die Elfen schließen den großen Sack.",
      "Der Schlitten ist startbereit.",
    ],
    traveling: [
      "Der Schlitten ist gerade über {city} hinweggeflogen.",
      "Der Weihnachtsmann verlässt {city} mit einem etwas leichteren Sack.",
      "Rudolf führt den Schlitten nach dem Halt in {city} weiter.",
      "In {city} haben sie ihre Geschenke schon bekommen.",
    ],
    completed: [
      "Der Weihnachtsmann hat seine Reise beendet.",
      "Alle Geschenke sind ausgeliefert.",
      "Die Rentiere ruhen sich bereits am Nordpol aus.",
      "Das nächste Weihnachten wird schon vorbereitet.",
    ],
    lastVisited: "Zuletzt besucht",
    nextStop: "Nächster Halt",
    flyingTo: "Flug Richtung {leg}",
    atNorthPole: "Am Nordpol",
    backHome: "Wieder zu Hause",
    legLabel: {
      "north-pole": "Nordpol",
      pacific: "Pazifik",
      oceania: "Ozeanien",
      "east-asia": "Ostasien",
      "southeast-asia": "Südostasien",
      "central-asia": "Zentral- und Südasien",
      "middle-east": "Naher Osten",
      "eastern-europe": "Osteuropa",
      africa: "Afrika",
      "western-europe": "Westeuropa",
      "south-america": "Südamerika",
      "north-america": "Nordamerika",
      "north-pole-return": "Nordpol",
    },
  },
  map: {
    heading: "Die Route des Weihnachtsmanns",
    subheading: "Tippe auf den Schlitten oder eine Stadt, um mehr zu erfahren.",
    ariaLabel: "Weltkarte mit der Route des Weihnachtsmanns",
    sleighLabel: "Schlitten des Weihnachtsmanns",
    northPoleLabel: "Nordpol",
    legend: {
      visited: "Schon besucht",
      current: "Er ist hier",
      upcoming: "Noch nicht",
    },
    stopStatus: {
      visited: "Der Weihnachtsmann war schon hier",
      current: "Der Weihnachtsmann ist gerade hier",
      upcoming: "Der Weihnachtsmann ist noch nicht angekommen",
    },
    localTime: "Ortszeit: {time}",
    close: "Schließen",
    followSleigh: "Schlitten verfolgen",
    wholeWorld: "Ganze Welt anzeigen",
    listHeading: "Die Reise, Schritt für Schritt",
    listIntro:
      "Dieselbe Reise als Liste – falls du lieber liest oder die Karte auf deinem Bildschirm nicht gut zu sehen ist.",
    parentsNote:
      "Hinweis für Eltern: Dies ist ein simuliertes Weihnachtserlebnis. Die Route wird anhand der Zeitzonen berechnet – sie zeigt weder einen echten Standort noch GPS-Tracking.",
  },
  eta: {
    heading: "Wann kommt er bei dir an?",
    body: "Der Weihnachtsmann kommt ungefähr zwischen {start} und {end} in deine Region.",
    zone: "Berechnet für deine Zeitzone ({zone}).",
    remaining: "Noch {countdown}.",
    passed:
      "Der Weihnachtsmann war schon bei dir. Schau unter dem Weihnachtsbaum nach!",
    unknownZone: "Berechnet anhand der Uhr deines Geräts.",
    disclaimer:
      "Das ist ein ungefährer Zeitraum, keine garantierte Uhrzeit: Der Weihnachtsmann kommt am liebsten, wenn alle schlafen.",
  },
  stats: {
    heading: {
      preparing: "Gerade in der Werkstatt",
      liftoff: "Alles bereit für die große Nacht",
      traveling: "Die Reise in Zahlen",
      completed: "So ist die Reise gelaufen",
    },
    labels: {
      giftsReady: "Geschenke bereit",
      giftsDelivered: "Geschenke ausgeliefert",
      lettersReceived: "Briefe erhalten",
      elvesWorking: "Elfen bei der Arbeit",
      reindeerTraining: "Rentiere im Training",
      kilometers: "Kilometer",
      citiesVisited: "Besuchte Städte",
      countriesVisited: "Besuchte Länder",
      homesWaiting: "Wartende Häuser",
      travelMinutes: "Flugzeit",
    },
    units: { kilometers: "km", hours: "Std." },
  },
  workshop: {
    heading: "Gerade am Nordpol",
    activities: {
      preparing: [
        "Die Elfen verpacken Geschenke.",
        "Der Weihnachtsmann liest Briefe.",
        "Die Rentiere trainieren auf der Startbahn.",
        "Frau Claus backt Kekse.",
        "Der Schlitten bekommt seine jährliche Wartung.",
        "Im Saal der großen Liste werden die Namen doppelt geprüft.",
      ],
      liftoff: [
        "Der Schlitten bekommt seine letzte Kontrolle.",
        "Rudolf trainiert für die große Nacht.",
        "Die Elfen laden die Säcke auf den Schlitten.",
        "Frau Claus bereitet heiße Schokolade für die Reise zu.",
      ],
      traveling: [
        "Die Elfen verfolgen die Reise vom Kontrollturm aus.",
        "Frau Claus lässt das Feuer brennen und wartet auf seine Rückkehr.",
        "In der Werkstatt wird das letzte Geschenkpapier aufgeräumt.",
        "Die Ersatzrentiere stehen bereit, falls sie gebraucht werden.",
      ],
      completed: [
        "Die Rentiere ruhen im Stall am Nordpol.",
        "Frau Claus serviert heiße Schokolade nach der Reise.",
        "Die Elfen räumen die Werkstatt für das nächste Jahr auf.",
        "Der Weihnachtsmann legt die große Liste bis zum nächsten Weihnachten weg.",
      ],
    },
    night: [
      "Am Nordpol ist Nacht: In der Werkstatt arbeitet man im Schein der Lampen.",
      "Die Elfen der Nachtschicht verpacken leise.",
      "Der Weihnachtsmann schläft ein wenig vor dem großen Tag.",
      "Man hört nur den knirschenden Schnee rund um die Werkstatt.",
    ],
  },
  surprises: {
    northPole:
      "Du hast die Nordlichter geweckt! Die Elfen winken dir aus der Werkstatt zu.",
    celebration: "Reise beendet! Frohe Weihnachten.",
  },
  controls: {
    soundOn: "Ton einschalten",
    soundOff: "Stummschalten",
    fullscreenEnter: "Vollbild",
    fullscreenExit: "Vollbild verlassen",
    share: "Teilen",
    shareCopied: "Link kopiert",
    motionOn: "Animationen einschalten",
    motionOff: "Animationen reduzieren",
  },
  share: {
    title: "Wo ist der Weihnachtsmann?",
    text: {
      preparing:
        "Finde heraus, wie lange es noch dauert, bis der Weihnachtsmann kommt.",
      liftoff: "Der Weihnachtsmann hebt gleich ab!",
      traveling: "Sieh, wo der Weihnachtsmann gerade ist!",
      completed:
        "Der Weihnachtsmann hat seine Reise beendet. Frohe Weihnachten!",
    },
  },
  faq: {
    heading: "Häufige Fragen",
    items: [
      {
        question: "Wie lange dauert es noch bis Weihnachten?",
        answer:
          "Der Countdown auf dieser Seite zeigt es dir sekundengenau. Heiligabend ist am 24. Dezember — dann kommt der Weihnachtsmann. Er startet am Nachmittag des 24. am Nordpol.",
      },
      {
        question: "Wo ist der Weihnachtsmann gerade?",
        answer:
          "Das hängt vom Tag ab. Die meiste Zeit des Jahres ist er am Nordpol und bereitet Weihnachten vor. An Heiligabend reist er um die ganze Welt. Die Karte zeigt, wo er sich gerade befinden sollte.",
      },
      {
        question: "Welche Route nimmt der Weihnachtsmann?",
        answer:
          "Er beginnt im Pazifik, fliegt weiter über Ozeanien und Asien, durchquert den Nahen Osten, Europa und Afrika, fliegt nach Südamerika und beendet die Reise in Nordamerika, bevor er nach Hause zurückkehrt. Er folgt der Nacht und liefert dort, wo es schon dunkel ist.",
      },
      {
        question: "Wann verlässt der Weihnachtsmann den Nordpol?",
        answer:
          "Er hebt am Nachmittag des 24. Dezember ab, wenn es auf den Pazifikinseln fast Mitternacht ist. Danach fliegt er ohne Pause bis zum Morgen des 25. Dezember.",
      },
      {
        question: "Wann kommt der Weihnachtsmann an?",
        answer:
          "In Deutschland kommt er am Nachmittag oder frühen Abend des 24. Dezember, meist während die Kinder unterwegs sind — ungefähr zwischen 16 und 20 Uhr. Diese Seite zeigt das ungefähre Zeitfenster für deine Zeitzone.",
      },
      {
        question: "Besucht der Weihnachtsmann jedes Land?",
        answer:
          "Ja – alle 195 Länder der Welt liegen auf seiner Route. Die Karte markiert einige Städte als Orientierungspunkte, aber der Schlitten fliegt wirklich überall hin.",
      },
      {
        question: "Wie schafft er die ganze Welt in einer Nacht?",
        answer:
          "Er fliegt nach Westen und folgt der Nacht. Weil er sich mit der Erdrotation bewegt, dauert sein Heiligabend mehr als dreißig Stunden – und ein wenig Magie vom Nordpol hilft natürlich auch.",
      },
      {
        question: "Was passiert, wenn ich bei seiner Ankunft wach bin?",
        answer:
          "Der Weihnachtsmann ist sehr vorsichtig: Wenn er merkt, dass jemand wach ist, fliegt er weiter und kommt später zurück. Am besten gehst du früh schlafen.",
      },
      {
        question: "Woher weiß er, wo ich wohne?",
        answer:
          "Aus den Briefen. Jeder Brief, der am Nordpol ankommt, wird mit Name und Ort in die große Liste eingetragen. Daraus planen die Elfen die Route.",
      },
      {
        question: "Wann kommt er zum Nordpol zurück?",
        answer:
          "Am Morgen des 25. Dezember, nach der letzten Auslieferung. Die Rentiere ruhen sich ein paar Tage aus und die Werkstatt beginnt mit den Vorbereitungen für das nächste Weihnachten.",
      },
      {
        question: "Zeigt die Karte seinen echten Standort?",
        answer:
          "Nein. Dies ist ein simuliertes Weihnachtserlebnis. Die Route wird anhand der Zeitzonen berechnet, damit sie zur Nacht an jedem Ort passt. Es gibt kein GPS-Tracking und keinen echten Standort; diese Seite fragt auch nie nach deinem Aufenthaltsort.",
      },
      {
        question: "Wie kann ich mit dem Weihnachtsmann sprechen?",
        answer:
          "Auf PapaNoel.chat kannst du einen Anruf vorbereiten, bei dem der Weihnachtsmann direkt und in Echtzeit mit deinem Kind spricht – sogar mit seinem Namen. Du entscheidest, was er vorher wissen darf.",
      },
      {
        question: "Kann ich einen Videoanruf mit dem Weihnachtsmann machen?",
        answer:
          "Ja. Zusätzlich zum Sprachanruf kannst du einen persönlichen Videoanruf einrichten, bei dem der Weihnachtsmann den Namen, die Interessen, Erfolge und Weihnachtswünsche deines Kindes kennt.",
      },
    ],
  },
  cta: {
    heading: "Während du wartest, kannst du mit dem Weihnachtsmann sprechen",
    body: "Bereite einen persönlichen Videoanruf vor, bei dem der Weihnachtsmann den Namen, die Interessen, Erfolge und Weihnachtswünsche deines Kindes kennt.",
    primary: "Mit dem Weihnachtsmann sprechen",
    secondary: "Magische Figuren entdecken",
    free: "Erst einmal ausprobieren? Sprich kostenlos mit dem Weihnachtsmann",
    travelingNote:
      "Der Weihnachtsmann ist heute Abend sehr beschäftigt, aber du kannst noch einen magischen Videoanruf vorbereiten.",
  },
  stops: {
    "north-pole": "Nordpol",
    "north-pole-return": "Nordpol",
    auckland: "Auckland",
    suva: "Suva",
    sydney: "Sydney",
    melbourne: "Melbourne",
    brisbane: "Brisbane",
    "port-moresby": "Port Moresby",
    darwin: "Darwin",
    tokyo: "Tokio",
    seoul: "Seoul",
    shanghai: "Shanghai",
    beijing: "Peking",
    "hong-kong": "Hongkong",
    manila: "Manila",
    singapore: "Singapur",
    jakarta: "Jakarta",
    bangkok: "Bangkok",
    dhaka: "Dhaka",
    kathmandu: "Kathmandu",
    "new-delhi": "Neu-Delhi",
    karachi: "Karatschi",
    kabul: "Kabul",
    dubai: "Dubai",
    tehran: "Teheran",
    riyadh: "Riad",
    istanbul: "Istanbul",
    moscow: "Moskau",
    athens: "Athen",
    warsaw: "Warschau",
    cairo: "Kairo",
    johannesburg: "Johannesburg",
    lagos: "Lagos",
    berlin: "Berlin",
    rome: "Rom",
    paris: "Paris",
    madrid: "Madrid",
    london: "London",
    lisbon: "Lissabon",
    reykjavik: "Reykjavík",
    "rio-de-janeiro": "Rio de Janeiro",
    "sao-paulo": "São Paulo",
    "buenos-aires": "Buenos Aires",
    santiago: "Santiago",
    "la-paz": "La Paz",
    caracas: "Caracas",
    bogota: "Bogotá",
    lima: "Lima",
    "new-york": "New York",
    toronto: "Toronto",
    havana: "Havanna",
    "mexico-city": "Mexiko-Stadt",
    chicago: "Chicago",
    denver: "Denver",
    "los-angeles": "Los Angeles",
    vancouver: "Vancouver",
    anchorage: "Anchorage",
    honolulu: "Honolulu",
  },
};
